/**
 * syllabusProgress — 학습 화면 「강의」 보기의 난이도 배지 계산(순수 함수).
 *
 * 설계: docs/learning-composition-v2/FE-20260914-hub-syllabus-difficulty-badges.html §6
 * 계약: docs/learning-composition-v2/BE-20260914-reply-student-syllabus-progress.md (GET /api/v2/syllabi/{id}/progress)
 *
 * 배지 숫자는 **풀 때마다(attempts)** 센 정답률이다(오너 결정 D6). 문제별 최근 결과(latest)는 말풍선에만 적는다.
 * 분모는 정답 + 오답 + 미응답 — 미응답은 채점됐으나 답이 없는 풀이라 맞힌 것으로 치지 않는다.
 * 난이도 없는 문제(none)는 배지로 그리지 않고 말풍선 끝에 풀이 수만 적는다.
 */
import { isPredeployError } from '../SyllabusV2/syllabusApi';
import { MATCH_LABEL } from '../SyllabusV2/syllabusQuery';

export const DIFFICULTIES = [1, 2, 3, 4, 5];
/** 풀이 수가 이보다 적으면 흐리게 — 한두 번의 결과가 정답률처럼 읽히지 않게 */
export const THIN_BELOW = 3;

const ZERO = { correct: 0, wrong: 0, unanswered: 0 };
const cellOf = (buckets, key) => ({ ...ZERO, ...(buckets && buckets[key]) });
const total = (c) => (c.correct || 0) + (c.wrong || 0) + (c.unanswered || 0);
const pct = (a, b) => Math.round((a / b) * 100);

/**
 * 행 식이 **모든 묶음**에서 난이도를 좁히면, 그 밖의 난이도(배지를 「조건 밖」으로 비운다).
 * 묶음 안의 난이도 항끼리는 그리고(교집합), 묶음끼리는 또는(합집합). 한 묶음이라도 난이도 항이 없으면 좁히지 않은 것이라 빈 배열.
 */
export function outOfConditionDifficulties(query) {
  const groups = Array.isArray(query?.groups) ? query.groups : [];
  if (!groups.length) return [];
  const inside = new Set();
  for (const g of groups) {
    const terms = (Array.isArray(g?.terms) ? g.terms : []).filter((t) => t?.field === 'difficulty');
    if (!terms.length) return [];
    let allowed = DIFFICULTIES;
    terms.forEach((t) => {
      const vs = new Set((t.values || []).map(Number));
      allowed = allowed.filter((d) => vs.has(d));
    });
    allowed.forEach((d) => inside.add(d));
  }
  return DIFFICULTIES.filter((d) => !inside.has(d));
}

/**
 * 행 식에 든 개념 번호 — 중복 없이 나온 순서대로. 행을 누르면 선택 개념을 이것으로 바꾼다.
 * 「제외」 항의 개념은 뺀다 — 행이 다루는 개념이 아니라 빼는 개념이다(「순번 100 이상 제외」 행이면 700개가 켜진다)
 */
export function conceptIdsOf(query) {
  const out = [];
  (Array.isArray(query?.groups) ? query.groups : []).forEach((g) => (Array.isArray(g?.terms) ? g.terms : []).forEach((t) => {
    if (t?.field !== 'concept' || t.match === 'none') return;
    (t.values || []).forEach((v) => {
      const n = Number(v);
      if (Number.isInteger(n) && !out.includes(n)) out.push(n);
    });
  }));
  return out;
}

/** 행 하나의 배지 다섯 — [{ d, kind: 'ok' | 'thin' | 'none' | 'out', rate, title }] */
export function badgeCells(node, query) {
  const out = new Set(outOfConditionDifficulties(query));
  const noneN = total(cellOf(node?.attempts, 'none'));
  const noneNote = noneN ? ` · 난이도 없는 문제 풀이 ${noneN}회는 배지에 없음` : '';
  return DIFFICULTIES.map((d) => {
    if (out.has(d)) return { d, kind: 'out', rate: null, title: `난이도 ${d} — 이 행의 조건 밖` };
    const a = cellOf(node?.attempts, String(d));
    const n = total(a);
    if (!n) return { d, kind: 'none', rate: null, title: `난이도 ${d} — 풀이 없음${noneNote}` };
    const rate = pct(a.correct, n);
    const head = [`난이도 ${d} · 풀이 ${n}회 — 정답 ${a.correct}`, `오답 ${a.wrong}`];
    if (a.unanswered) head.push(`미응답 ${a.unanswered}`);
    let title = `${head.join(' · ')} · 정답률 ${rate}%`;
    const l = cellOf(node?.latest, String(d));
    const m = total(l);
    if (m) title += ` · 최근 결과 기준 문제 ${m}개 중 ${l.correct}개(${pct(l.correct, m)}%)`;
    return { d, kind: n < THIN_BELOW ? 'thin' : 'ok', rate, title: title + noneNote };
  });
}

// 개념을 요구하는 항 — 「제외」 항은 아래 방식 단추가 다루지 않는다. 돌리면 빼던 개념을 요구하는 식으로 뒤집힌다
const isConceptReq = (t) => t?.field === 'concept' && t.match !== 'none';

/**
 * 행 식의 개념 조건 방식 — 강의에 저장된 그대로 「하나라도」·「모두」·「정확히」(syllabusQuery.MATCH_LABEL).
 * 개념 항마다 다르면 가운뎃점으로 잇는다. 개념을 요구하는 항이 없으면 ''(「제외」 항은 세지 않는다).
 * 오른쪽 문제 미리보기의 강의 행 보기가 개념 칩 옆에 적는다(D10)
 */
export function conceptMatchLabel(query) {
  const out = [];
  (Array.isArray(query?.groups) ? query.groups : []).forEach((g) => (Array.isArray(g?.terms) ? g.terms : []).forEach((t) => {
    if (!isConceptReq(t)) return;
    const label = MATCH_LABEL[t.match] || MATCH_LABEL.any;
    if (!out.includes(label)) out.push(label);
  }));
  return out.join('·');
}

/** 오른쪽 목록의 개념 조건 단추가 도는 순서 — 하나라도 → 모두 → 정확히 */
export const CONCEPT_MATCH_ORDER = ['any', 'all', 'exact'];

/** 개념을 요구하는 항의 방식이 모두 같으면 그 방식('any'·'all'·'exact'), 섞였거나 그런 항이 없으면 null */
export function uniformConceptMatch(query) {
  const set = new Set();
  (Array.isArray(query?.groups) ? query.groups : []).forEach((g) => (Array.isArray(g?.terms) ? g.terms : []).forEach((t) => {
    if (isConceptReq(t)) set.add(CONCEPT_MATCH_ORDER.includes(t.match) ? t.match : 'any');
  }));
  return set.size === 1 ? [...set][0] : null;
}

/**
 * 개념을 요구하는 항의 방식만 바꾼 식 — 「제외」 항·나머지 항·값은 그대로, 원본은 건드리지 않는다
 * (오른쪽 목록에서 방식을 바꿔 다시 물을 때, 강의에는 저장하지 않음)
 */
export function withConceptMatch(query, match) {
  if (!query || !Array.isArray(query.groups)) return query;
  return {
    ...query,
    groups: query.groups.map((g) => ({
      ...g,
      terms: (Array.isArray(g?.terms) ? g.terms : []).map((t) => (isConceptReq(t) ? { ...t, match } : t)),
    })),
  };
}

/** 행 식이 가리키는 값 중 지금 없는 것의 수 — 행 끝 「사라진 항목」 표시(회신 §6-3 권고) */
export function lostCount(node) {
  return (Array.isArray(node?.warnings) ? node.warnings : [])
    .reduce((s, w) => s + (Array.isArray(w?.unknown_values) ? w.unknown_values.length : 0), 0);
}

/** 응답 → 줄 번호(문자열 — 나무 색인과 같은 열쇠)별로 찾을 수 있게 */
export function indexProgress(res) {
  const byNode = new Map();
  const skipped = new Map();
  (Array.isArray(res?.nodes) ? res.nodes : []).forEach((n) => { if (n && n.node_id != null) byNode.set(String(n.node_id), n); });
  (Array.isArray(res?.skipped) ? res.skipped : []).forEach((k) => { if (k && k.node_id != null) skipped.set(String(k.node_id), k); });
  return {
    byNode,
    skipped,
    recordsProblems: Number.isInteger(res?.records_problems) ? res.records_problems : null,
    revision: res?.syllabus_revision ?? null,
  };
}

/**
 * 집계 실패 → 화면 상태(설계 §6 표). retry 는 [다시 시도]를 보일지.
 * 404 는 강의 없음·학생 없음에 사유 코드가 없어 가르지 않는다 — 나무 조회가 먼저 성공한 뒤라 대개 학생 쪽이다.
 * 408 문구는 강의 검색용(「5초」)을 쓰지 않는다 — 이 집계의 상한은 요청 전체 30초다.
 */
export function progressErrorState(err) {
  if (isPredeployError(err)) return { kind: 'predeploy', message: '서버에 아직 강의 배지 집계가 배포되지 않았습니다.', retry: false };
  if (err?.reason === 'out_of_scope') return { kind: 'syllabus_gone', message: '볼 수 있는 범위 밖의 강의입니다.', retry: false };
  if (err?.reason === 'student_out_of_scope') return { kind: 'student_scope', message: '담당 학생이 아니어서 이 학생의 배지를 볼 수 없습니다.', retry: false };
  if (err?.httpStatus === 408) return { kind: 'timeout', message: '집계가 30초를 넘어 멈췄습니다.', retry: true };
  if (err?.httpStatus === 404) return { kind: 'missing', message: '강의나 학생을 찾을 수 없습니다.', retry: true };
  return { kind: 'error', message: err?.message || '배지를 불러오지 못했습니다.', retry: true };
}
